import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';

interface Supplier { id: number; company_name: string; contact_person?: string; phone?: string; email?: string; }
interface Product {
  id: number; name: string; name_ru?: string; name_en?: string; code: string; unit: string; purchase_price: number;
  hs_code_manufacturer?: string; hs_code_uz?: string;
  manufacturer?: string; part_number?: string; model_number?: string; country_of_origin?: string;
}
interface PurchaseItem {
  product_id?: number; product_name: string; product_name_ru?: string; product_name_en?: string;
  product_code: string; hs_code_manufacturer?: string; hs_code_uz?: string;
  product_manufacturer?: string; part_number?: string; model_number?: string; country_of_origin?: string;
  unit: string; quantity: number; unit_price: number; total_price: number;
}
interface AssignableUser { id: number; full_name: string; role: string; role_label?: string; }
interface WarehouseShort { id: number; name: string; }
interface Purchase {
  id: number; number: string; supplier_id: number; supplier?: Supplier; status: string; currency: string;
  expected_date?: string; notes?: string; total_amount: number; items: PurchaseItem[];
  created_by?: number; creator?: { id: number; full_name: string };
  assigned_to_id?: number; assignee?: { id: number; full_name: string };
  created_at: string; tracking_number?: string; tracking_carrier?: string; tracking_status?: string;
  tracking_url?: string; shipped_date?: string; customs_date?: string; delivered_date?: string; received_date?: string;
  warehouse_id?: number;
}

const ROLE_GROUP_ORDER = ['admin','director','sales_head','sales_manager','service_head','service_engineer','warehouse_head','logistic_head','logist','chief_accountant','accountant','hr_manager','lawyer'];

const statusConfig: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Qoralama',      color: 'bg-gray-100 text-gray-700' },
  sent:      { label: 'Yuborildi',     color: 'bg-blue-100 text-blue-700' },
  confirmed: { label: 'Tasdiqlandi',   color: 'bg-yellow-100 text-yellow-700' },
  received:  { label: 'Qabul qilindi', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Bekor qilindi', color: 'bg-red-100 text-red-700' },
};

const trackingConfig: Record<string, { label: string; color: string; icon: string; date_field: string }> = {
  ordered:   { label: 'Buyurtma',   color: 'bg-blue-100 text-blue-700 border-blue-300',     icon: '📋', date_field: 'created_at' },
  shipped:   { label: "Jo'natildi", color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: '🚢', date_field: 'shipped_date' },
  customs:   { label: 'Bojxona',    color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '🛃', date_field: 'customs_date' },
  delivered: { label: 'Yetkazildi', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: '📦', date_field: 'delivered_date' },
  received:  { label: 'Qabul',      color: 'bg-green-100 text-green-700 border-green-300',   icon: '✅', date_field: 'received_date' },
};

const TRACKING_STEPS = ['ordered', 'shipped', 'customs', 'delivered', 'received'];
const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };
const unitLabels: Record<string, string> = { piece: 'Dona', box: 'Quti', pack: 'Paket', liter: 'Litr', kg: 'Kg', meter: 'Metr', hour: 'Soat', service_unit: 'Xizmat' };
const emptyItem: PurchaseItem = { product_name: '', product_name_ru: '', product_name_en: '', product_code: '', hs_code_manufacturer: '', hs_code_uz: '', product_manufacturer: '', part_number: '', model_number: '', country_of_origin: '', unit: '', quantity: 1, unit_price: 0, total_price: 0 };

const Purchases: React.FC = () => {
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const canFull = canEdit('purchases');
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Purchase | null>(null);
  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState<Purchase | null>(null);
  const [pdfLang, setPdfLang] = useState<'uz' | 'ru' | 'en'>('uz');
  const [productSearch, setProductSearch] = useState<Record<number, string>>({});
  const [showProductDropdown, setShowProductDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [trackingForm, setTrackingForm] = useState({ tracking_number: '', tracking_carrier: '', tracking_url: '', tracking_status: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseShort[]>([]);
  const [form, setForm] = useState({ supplier_id: '', currency: 'usd', expected_date: '', notes: '', assigned_to_id: '', warehouse_id: '' });
  const [items, setItems] = useState<PurchaseItem[]>([{ ...emptyItem }]);

  const fetchData = async () => {
    try { const r = await api.get('/warehouses'); setWarehouses(r.data); } catch {}
    try { const p = await api.get('/purchases'); setPurchases(p.data); } catch (e) { console.error('Purchases:', e); }
    try { const s = await api.get('/suppliers'); setSuppliers(s.data); } catch { setSuppliers([]); }
    try { const pr = await api.get('/products'); setProducts(pr.data); } catch { setProducts([]); }
  };

  const fetchAssignableUsers = async () => {
    try { const r = await api.get('/users/assignable'); setAssignableUsers(r.data); } catch { setAssignableUsers([]); }
  };

  useEffect(() => { fetchData(); fetchAssignableUsers(); }, []);

  // Dashboard "So'nggi faoliyat" feed havolasi ?open=<id> bilan keladi - shu
  // xarid so'rovini to'g'ridan-to'g'ri detal oynasida ochadi. Ruxsat bo'lmasa
  // (403) - jim o'tkazib yuboriladi.
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) {
      api.get(`/purchases/${openId}`).then(res => setShowDetail(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowProductDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = purchases.filter(p => {
    const supplierName = p.supplier?.company_name || '';
    const matchSearch = p.number.toLowerCase().includes(search.toLowerCase()) || supplierName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? p.status === filterStatus : true;
    return matchSearch && matchStatus;
  });

  const getFilteredProducts = (index: number) => {
    const s = (productSearch[index] || '').toLowerCase();
    if (!s) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(s) ||
      (p.name_ru || '').toLowerCase().includes(s) ||
      (p.name_en || '').toLowerCase().includes(s) ||
      p.code.toLowerCase().includes(s)
    );
  };

  const calcItemTotal = (item: PurchaseItem) => Math.round(item.unit_price * item.quantity * 100) / 100;
  const totalAmount = items.reduce((sum, item) => sum + calcItemTotal(item), 0);

  const updateItem = (index: number, field: keyof PurchaseItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') newItems[index].total_price = calcItemTotal(newItems[index]);
    setItems(newItems);
  };

  const selectProductFromSearch = (index: number, product: Product) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], product_id: product.id, product_name: product.name, product_name_ru: product.name_ru || '', product_name_en: product.name_en || '', product_code: product.code, hs_code_manufacturer: product.hs_code_manufacturer || '', hs_code_uz: product.hs_code_uz || '', product_manufacturer: product.manufacturer || '', part_number: product.part_number || '', model_number: product.model_number || '', country_of_origin: product.country_of_origin || '', unit: product.unit, unit_price: product.purchase_price, total_price: product.purchase_price * newItems[index].quantity };
    setItems(newItems);
    setProductSearch(prev => ({ ...prev, [index]: product.name }));
    setShowProductDropdown(null);
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    setProductSearch(prev => { const n = { ...prev }; delete n[index]; return n; });
  };

  const openAdd = () => {
    setForm({ supplier_id: '', currency: 'usd', expected_date: '', notes: '', assigned_to_id: '', warehouse_id: '' });
    setItems([{ ...emptyItem }]);
    setProductSearch({});
    setEditId(null); setError(''); setShowModal(true);
  };

  const openEdit = (p: Purchase) => {
    setForm({ supplier_id: p.supplier_id.toString(), assigned_to_id: p.assigned_to_id?.toString() || '', warehouse_id: p.warehouse_id?.toString() || '', currency: p.currency, expected_date: p.expected_date ? p.expected_date.split('T')[0] : '', notes: p.notes || '' });
    const pItems = p.items.length > 0 ? p.items.map(i => ({ ...i })) : [{ ...emptyItem }];
    setItems(pItems);
    const searches: Record<number, string> = {};
    pItems.forEach((item, idx) => { searches[idx] = item.product_name; });
    setProductSearch(searches);
    setEditId(p.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.supplier_id) { setError("Yetkazib beruvchi tanlanishi kerak"); return; }
    if (items.every(i => !i.product_name)) { setError("Kamida bitta mahsulot qo'shing"); return; }
    setLoading(true); setError('');
    try {
      const payload: any = { ...form, assigned_to_id: form.assigned_to_id ? Number(form.assigned_to_id) : null, warehouse_id: form.warehouse_id ? Number(form.warehouse_id) : null, supplier_id: Number(form.supplier_id), expected_date: form.expected_date ? new Date(form.expected_date).toISOString() : null, items: items.filter(i => i.product_name).map(i => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) };
      if (editId) await api.put(`/purchases/${editId}`, payload);
      else await api.post('/purchases', payload);
      setShowModal(false); fetchData();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xatolik yuz berdi'); }
    finally { setLoading(false); }
  };

  const handleTrackingUpdate = async (id: number, data: any) => {
    try {
      const cleanData: any = {};
      Object.entries(data).forEach(([k, v]) => { cleanData[k] = v === '' ? null : v; });
      await api.put(`/purchases/${id}`, cleanData);
      await fetchData();
      const updated = (await api.get(`/purchases/${id}`)).data;
      setShowDetail(updated);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) alert(detail.map((d: any) => d.msg).join('\n'));
      else alert(detail || 'Xato yuz berdi');
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    if (!canFull) return;
    await api.put(`/purchases/${id}`, { status });
    fetchData();
    if (showDetail) { const res = await api.get(`/purchases/${id}`); setShowDetail(res.data); }
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/purchases/${id}`);
    setShowDetail(null); fetchData();
  };

  const handleExportPdf = (p: Purchase) => { setShowPdfModal(p); };

  const doExportPdf = async () => {
    if (!showPdfModal) return;
    const res = await api.get(`/purchases/${showPdfModal.id}/export/pdf`, { params: { lang: pdfLang }, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `${showPdfModal.number}_${pdfLang}.pdf`; a.click();
    setShowPdfModal(null);
  };

  const handleCreateContract = async (id: number, number: string) => {
    if (!window.confirm(`${number} asosida shartnoma yaratilasinmi?`)) return;
    try {
      const res = await api.post(`/purchases/${id}/from-purchase`);
      const cNumber = res.data.number;
      if (window.confirm(`✅ Shartnoma yaratildi: ${cNumber}\n\nBuxgalteriya → Shartnomalar bo'limiga o'tish uchun OK bosing.`)) navigate('/accounting?tab=purchase');
    } catch (e: any) { alert(e.response?.data?.detail || 'Xato yuz berdi'); }
  };

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Xarid so'rovlari</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + <span className="hidden sm:inline">Yangi so'rov</span>
        </button>
      </div>

      {/* Status kartalar */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 mb-4 md:mb-6">
        {Object.entries(statusConfig).map(([key, val]) => (
          <div key={key} className="bg-white rounded-xl shadow p-3 md:p-4">
            <p className="text-gray-500 text-xs truncate">{val.label}</p>
            <p className="text-xl md:text-2xl font-bold text-gray-800">{purchases.filter(p => p.status === key).length}</p>
          </div>
        ))}
      </div>

      {/* Filtrlar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input type="text" placeholder="🔍 Raqam yoki yetkazib beruvchi..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha holatlar</option>
          {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Jadval */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 py-3 text-left">Raqam / Supplier</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Sana</th>
              <th className="px-3 py-3 text-left hidden lg:table-cell">Kutilgan</th>
              <th className="px-3 py-3 text-left">Holat</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Mas'ul</th>
              <th className="px-3 py-3 text-right">Summa</th>
              <th className="px-3 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">So'rovlar yo'q</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-3">
                  <div className="font-mono text-blue-600 font-medium text-xs">{p.number}</div>
                  <div className="font-medium text-gray-800 text-sm">{p.supplier?.company_name || '—'}</div>
                  <div className="md:hidden text-xs text-gray-400 mt-0.5">
                    {new Date(p.created_at).toLocaleDateString('ru-RU')}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-500 text-xs hidden md:table-cell">{new Date(p.created_at).toLocaleDateString('ru-RU')}</td>
                <td className="px-3 py-3 text-gray-500 text-xs hidden lg:table-cell">{p.expected_date ? new Date(p.expected_date).toLocaleDateString('ru-RU') : '—'}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig[p.status]?.color}`}>
                    {statusConfig[p.status]?.label}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-600 text-xs hidden md:table-cell">{p.assignee?.full_name || '—'}</td>
                <td className="px-3 py-3 text-right font-bold whitespace-nowrap text-xs">
                  {p.total_amount.toLocaleString()} {currencySymbol[p.currency]}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(p)} className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => handleExportPdf(p)} className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">📄</button>
                    <button onClick={() => canFull && openEdit(p)} disabled={!canFull}
                      className={`p-1.5 rounded text-xs ${canFull ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>✏️</button>
                    <button onClick={() => handleDelete(p.id)} disabled={!canFull}
                      className={`p-1.5 rounded text-xs ${canFull ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDF Til tanlash Modal — boshqa modallar (masalan Detail) ustida ochilishi mumkin, shuning uchun yuqoriroq z-index */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-2">📄 PDF tili</h3>
            <p className="text-gray-500 text-sm mb-4">{showPdfModal.number} — tanlangan tilda mahsulot nomi ko'rsatiladi (inglizcha nom har doim qo'shiladi)</p>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {(['uz', 'ru', 'en'] as const).map(lang => (
                <button key={lang} onClick={() => setPdfLang(lang)}
                  className={`py-3 rounded-xl border-2 text-sm font-medium transition ${pdfLang === lang ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  {lang === 'uz' ? "🇺🇿 O'zbek" : lang === 'ru' ? '🇷🇺 Rus' : '🇬🇧 Ingliz'}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowPdfModal(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={doExportPdf} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">📥 Yuklab olish</button>
            </div>
          </div>
        </div>
      )}

      {/* Yaratish/Tahrirlash Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-4 md:p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Tahrirlash' : "Yangi xarid so'rovi"}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib beruvchi *</label>
                <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD — Dollar</option>
                  <option value="eur">EUR — Euro</option>
                  <option value="uzs">UZS — So'm</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kutilgan sana</label>
                <input type="date" value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul hodim</label>
                <select value={form.assigned_to_id} onChange={e => setForm({ ...form, assigned_to_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {Array.from(new Set(assignableUsers.map(u => u.role))).sort((a, b) => { const ia = ROLE_GROUP_ORDER.indexOf(a); const ib = ROLE_GROUP_ORDER.indexOf(b); return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib); }).map(role => {
                    const groupUsers = assignableUsers.filter(u => u.role === role);
                    return <optgroup key={role} label={groupUsers[0]?.role_label || role}>{groupUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</optgroup>;
                  })}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Omborxona</label>
                <select value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>🏪 {w.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* Mahsulotlar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700 text-sm">Mahsulotlar</h4>
                <button onClick={addItem} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm">+ Qo'shish</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs">
                    <tr>
                      <th className="px-2 py-2 text-left w-6">#</th>
                      <th className="px-2 py-2 text-left">Mahsulot</th>
                      <th className="px-2 py-2 text-center w-14">Birlik</th>
                      <th className="px-2 py-2 text-center w-16">Miqdor</th>
                      <th className="px-2 py-2 text-center w-24">Narx</th>
                      <th className="px-2 py-2 text-right w-24">Jami</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-2 py-2 text-gray-400 text-xs">{index + 1}</td>
                        <td className="px-2 py-2 align-top">
                          <div className="relative" ref={showProductDropdown === index ? dropdownRef : null}>
                            <input
                              value={productSearch[index] ?? item.product_name}
                              onChange={e => { setProductSearch(prev => ({ ...prev, [index]: e.target.value })); setShowProductDropdown(index); updateItem(index, 'product_name', e.target.value); }}
                              onFocus={() => setShowProductDropdown(index)}
                              placeholder="🔍 Mahsulot qidiring..."
                              className={`w-full border rounded px-2 py-1.5 text-xs mb-1 focus:outline-none focus:ring-1 focus:ring-blue-500 ${item.product_id ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}`} />
                            {showProductDropdown === index && (
                              <div className="absolute z-50 w-64 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto mt-0.5">
                                {getFilteredProducts(index).length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-gray-400">Topilmadi</div>
                                ) : getFilteredProducts(index).map(p => (
                                  <button key={p.id} onClick={() => selectProductFromSearch(index, p)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b last:border-0">
                                    <div className="font-medium text-gray-800">{p.name}</div>
                                    {p.name_ru && <div className="text-gray-400">{p.name_ru}</div>}
                                    <div className="text-blue-500 font-mono">{p.code}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <input value={item.product_name_ru || ''} onChange={e => updateItem(index, 'product_name_ru', e.target.value)}
                            placeholder="Название (Рус)" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.product_name_en || ''} onChange={e => updateItem(index, 'product_name_en', e.target.value)}
                            placeholder="Name (English)" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.hs_code_manufacturer || ''} onChange={e => updateItem(index, 'hs_code_manufacturer', e.target.value)}
                            placeholder="HS kod (ishlab chiqaruvchi)" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.hs_code_uz || ''} onChange={e => updateItem(index, 'hs_code_uz', e.target.value)}
                            placeholder="HS kod (O'zbekiston)" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.part_number || ''} onChange={e => updateItem(index, 'part_number', e.target.value)}
                            placeholder="Katalog raqami" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.product_manufacturer || ''} onChange={e => updateItem(index, 'product_manufacturer', e.target.value)}
                            placeholder="Brend" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.model_number || ''} onChange={e => updateItem(index, 'model_number', e.target.value)}
                            placeholder="Model/Kod" className="w-full border rounded px-2 py-1 text-xs mb-1 focus:outline-none" />
                          <input value={item.country_of_origin || ''} onChange={e => updateItem(index, 'country_of_origin', e.target.value)}
                            placeholder="Ishlab chiqarilgan mamlakat" className="w-full border rounded px-2 py-1 text-xs focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">                          <input value={unitLabels[item.unit] || item.unit} onChange={e => updateItem(index, 'unit', e.target.value)}
                            className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" min="0" value={item.quantity} onChange={e => updateItem(index, 'quantity', Number(e.target.value))}
                            className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" min="0" value={item.unit_price} onChange={e => updateItem(index, 'unit_price', Number(e.target.value))}
                            className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                        </td>
                        <td className="px-2 py-2 text-right font-medium text-green-600 text-xs">{calcItemTotal(item).toLocaleString()}</td>
                        <td className="px-2 py-2">{items.length > 1 && <button onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 text-xs">✕</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2">
                      <td colSpan={5} className="px-2 py-2 text-right font-bold text-sm">JAMI:</td>
                      <td className="px-2 py-2 text-right font-bold text-blue-600">{totalAmount.toLocaleString()} {currencySymbol[form.currency]}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg md:text-xl font-bold font-mono">{showDetail.number}</h3>
                <p className="text-gray-500 text-sm">{showDetail.supplier?.company_name}</p>
                {showDetail.supplier?.phone && <p className="text-blue-500 text-xs">{showDetail.supplier.phone}</p>}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[showDetail.status]?.color}`}>
                {statusConfig[showDetail.status]?.label}
              </span>
            </div>

            <div className="flex gap-2 mb-4 flex-wrap">
              {Object.entries(statusConfig).map(([key, val]) => (
                <button key={key} onClick={() => handleStatusChange(showDetail.id, key)} disabled={showDetail.status === key || !canFull}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${showDetail.status === key || !canFull ? 'opacity-50 cursor-not-allowed border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>
                  {val.label}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="px-3 py-2 text-left">Mahsulot</th>
                    <th className="px-3 py-2 text-center">Miqdor</th>
                    <th className="px-3 py-2 text-center hidden sm:table-cell">Narx</th>
                    <th className="px-3 py-2 text-right">Jami</th>
                  </tr>
                </thead>
                <tbody>
                  {showDetail.items.map((item, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2">
                        <div className="font-medium text-sm">{item.product_name}</div>
                        {item.product_code && <div className="text-xs text-gray-400">{item.product_code}</div>}
                        {(item.hs_code_manufacturer || item.hs_code_uz) && (
                          <div className="text-xs text-gray-400">
                            🛃{item.hs_code_manufacturer && ` M: ${item.hs_code_manufacturer}`}{item.hs_code_uz && ` · UZ: ${item.hs_code_uz}`}
                          </div>
                        )}
                        {(item.part_number || item.product_manufacturer || item.model_number || item.country_of_origin) && (
                          <div className="text-xs text-gray-400">
                            {item.part_number && `Kataloq: ${item.part_number}`}
                            {item.product_manufacturer && `${item.part_number ? ' · ' : ''}Brend: ${item.product_manufacturer}`}
                            {item.model_number && ` · Model: ${item.model_number}`}
                            {item.country_of_origin && ` · ${item.country_of_origin}`}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs">{item.quantity} {unitLabels[item.unit] || item.unit}</td>
                      <td className="px-3 py-2 text-center text-xs hidden sm:table-cell">{item.unit_price.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-medium text-xs">{item.total_price.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2">
                    <td colSpan={3} className="px-3 py-2 text-right font-bold text-sm">JAMI:</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-600">{showDetail.total_amount.toLocaleString()} {currencySymbol[showDetail.currency]}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {showDetail.notes && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-600"><b>Izoh:</b> {showDetail.notes}</div>
            )}

            {(showDetail.assignee || showDetail.creator) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-4">
                {showDetail.assignee && <span>👤 <b className="text-gray-700">Mas'ul:</b> {showDetail.assignee.full_name}</span>}
                {showDetail.creator && <span>✍️ <b className="text-gray-700">Yaratuvchi:</b> {showDetail.creator.full_name}</span>}
              </div>
            )}

            {/* Tracking */}
            <div className="border rounded-xl p-3 mb-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-gray-700 text-sm">🚚 Yetkazib berish holati</h4>
                {canFull && (
                  <button onClick={() => { setTrackingForm({ tracking_number: showDetail.tracking_number || '', tracking_carrier: showDetail.tracking_carrier || '', tracking_url: showDetail.tracking_url || '', tracking_status: showDetail.tracking_status || '' }); setShowTrackingForm(!showTrackingForm); }}
                    className="text-xs px-2 py-1 bg-white border rounded-lg hover:bg-gray-50">✏️ Tracking</button>
                )}
              </div>
              <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
                {TRACKING_STEPS.map((step, idx) => {
                  const cfg = trackingConfig[step];
                  const currentIdx = showDetail.tracking_status ? TRACKING_STEPS.indexOf(showDetail.tracking_status) : -1;
                  const isDone = currentIdx >= idx;
                  const isCurrent = showDetail.tracking_status === step;
                  return (
                    <div key={step} className="flex items-center">
                      <div className="flex flex-col items-center min-w-16">
                        <button onClick={() => canFull && handleTrackingUpdate(showDetail.id, { tracking_status: step })} disabled={!canFull}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 transition ${isCurrent ? 'border-green-500 bg-green-100 scale-110' : isDone ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'} ${canFull ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}>
                          {cfg.icon}
                        </button>
                        <span className={`text-xs mt-1 text-center leading-tight ${isCurrent ? 'text-green-600 font-bold' : isDone ? 'text-blue-600' : 'text-gray-400'}`}>{cfg.label}</span>
                      </div>
                      {idx < TRACKING_STEPS.length - 1 && <div className={`h-0.5 w-4 mx-1 mb-4 ${currentIdx > idx ? 'bg-blue-400' : 'bg-gray-200'}`} />}
                    </div>
                  );
                })}
              </div>
              {(showDetail.tracking_number || showDetail.tracking_carrier) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
                  {showDetail.tracking_number && <span>📦 <b>Raqam:</b> {showDetail.tracking_number}</span>}
                  {showDetail.tracking_carrier && <span>🚛 <b>Carrier:</b> {showDetail.tracking_carrier}</span>}
                  {showDetail.tracking_url && <a href={showDetail.tracking_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">🔗 Kuzatish</a>}
                </div>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                {showDetail.shipped_date && <span>🚢 Jo'natildi: {new Date(showDetail.shipped_date).toLocaleDateString('ru-RU')}</span>}
                {showDetail.customs_date && <span>🛃 Bojxona: {new Date(showDetail.customs_date).toLocaleDateString('ru-RU')}</span>}
                {showDetail.delivered_date && <span>📦 Yetkazildi: {new Date(showDetail.delivered_date).toLocaleDateString('ru-RU')}</span>}
                {showDetail.received_date && <span>✅ Qabul: {new Date(showDetail.received_date).toLocaleDateString('ru-RU')}</span>}
              </div>
              {showTrackingForm && canFull && (
                <div className="mt-3 pt-3 border-t grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Tracking raqami</label>
                    <input value={trackingForm.tracking_number} onChange={e => setTrackingForm({...trackingForm, tracking_number: e.target.value})}
                      placeholder="1Z999AA10123456784" className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Carrier (DHL, FedEx...)</label>
                    <input value={trackingForm.tracking_carrier} onChange={e => setTrackingForm({...trackingForm, tracking_carrier: e.target.value})}
                      placeholder="DHL" className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-0.5">Kuzatish URL</label>
                    <input value={trackingForm.tracking_url} onChange={e => setTrackingForm({...trackingForm, tracking_url: e.target.value})}
                      placeholder="https://www.dhl.com/track?..." className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="sm:col-span-2 flex justify-end gap-2">
                    <button onClick={() => setShowTrackingForm(false)} className="px-3 py-1 border rounded text-xs hover:bg-gray-50">Bekor</button>
                    <button onClick={() => { handleTrackingUpdate(showDetail.id, trackingForm); setShowTrackingForm(false); }} className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">Saqlash</button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <button onClick={() => handleExportPdf(showDetail)} className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">📄 PDF</button>
              <button onClick={() => canFull && handleCreateContract(showDetail.id, showDetail.number)} disabled={!canFull}
                className={`px-3 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'}`}>📝 Shartnoma</button>
              <button onClick={() => navigate('/accounting?tab=purchase')} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm">📋 Shartnomaga</button>
              <button onClick={() => { if (canFull) { setShowDetail(null); openEdit(showDetail); } }} disabled={!canFull}
                className={`px-3 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>✏️ Tahrirlash</button>
              <button onClick={() => setShowDetail(null)} className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;
